import React from 'react';
import DOMPurify from 'dompurify';

// Renders HTML produced by RichTextField.js. react-simple-wysiwyg
// deliberately doesn't sanitize what it saves (see its own README), so
// this is the one place that does — always sanitize here, at the point
// HTML is actually injected into the page, rather than trusting whatever
// was stored (which could have reached the database some other way than
// this editor). See ui-architecture.md's "Rich Text Notes Field" section.
// <style> elements are dropped: an Email Sync Activity stores the email's
// own HTML, whose stylesheet would otherwise restyle the whole page once
// injected, and read as CSS text in htmlToPreviewText's preview. Inline
// style attributes are kept.
export function sanitizeHtml(html) {
  return DOMPurify.sanitize(html || '', { FORBID_TAGS: ['style'] });
}

// Flattens rich-text HTML to plain text for a truncated preview (e.g. a
// collapsed Activity row) — `-webkit-line-clamp` only has defined behaviour
// for inline content; applied directly to raw HTML whose notes include a
// bullet list, Chrome clips list markers inconsistently once the clamp cuts
// mid-list. Flattening first (bullets become "• " prefixes, numbered items
// "1. ") sidesteps that, since the clamp then only ever sees plain text.
// Block boundaries become line breaks, so a list still reads one item per
// line when rendered with `white-space: pre-line`.
export function htmlToPreviewText(html) {
  const doc = new DOMParser().parseFromString(sanitizeHtml(html), 'text/html');
  doc.querySelectorAll('li').forEach((li) => {
    const list = li.parentElement;
    const marker = list?.tagName === 'OL' ? `${Array.prototype.indexOf.call(list.children, li) + 1}. ` : '• ';
    li.textContent = `${marker}${li.textContent}`;
  });
  doc.body.querySelectorAll('p, li, div, br').forEach((el) => { el.after(document.createTextNode('\n')); });
  return (doc.body.textContent || '')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

// Same plain-text email artefacts emailSyncService.plainTextToHtml drops at
// sync time (Gmail's "<http://…>"/"<+612%20…>" link targets and "[image: …]"
// placeholders) — applied to a synced Activity's preview too, so ones logged
// before that cleanup existed read cleanly as well.
export function cleanEmailPreviewText(text) {
  return text
    .replace(/\[image:[^\]]*\]/gi, '')
    .replace(/<(?:(?:https?|mailto|tel):[^<>\s]*|www\.[^<>\s]*|\+?[\d%]+)>/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// A link's address as typed into the editor's Link dialog — a bare
// "acme.com.au" becomes https://acme.com.au, same as ExternalLink.js's
// absoluteUrl. Anything with a scheme (https:, mailto:) and Optim's own
// "/..." or "#..." links are left as they are.
export function linkHref(href) {
  const h = String(href || '').trim();
  if (!h || h.startsWith('/') || h.startsWith('#') || /^[a-z][a-z0-9+.-]*:/i.test(h)) return h;
  return `https://${h}`;
}

// lucide's ExternalLink icon as markup, since it's added to HTML here
// rather than rendered as a React element.
const EXTERNAL_LINK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:-1px;margin-left:3px" aria-hidden="true"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>';

// Every link leaving Optim follows ui-architecture.md's "External Link"
// standard: new tab, ⧉ icon after its text. An @mention (a[data-entity-id])
// or any other "/..." link stays a plain same-tab link to a page inside
// Optim. Image-only links (common in synced emails) get no icon. Runs after
// sanitizeHtml, which would otherwise strip the target attribute.
function withExternalLinks(html) {
  const doc = new DOMParser().parseFromString(sanitizeHtml(html), 'text/html');
  doc.querySelectorAll('a[href]').forEach((a) => {
    if (a.hasAttribute('data-entity-id')) return;
    const href = linkHref(a.getAttribute('href'));
    if (!href || href.startsWith('/') || href.startsWith('#')) return;
    a.setAttribute('href', href);
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
    if (a.textContent.trim()) a.insertAdjacentHTML('beforeend', EXTERNAL_LINK_ICON);
  });
  return doc.body.innerHTML;
}

function RichText({ html, style, className }) {
  return <div className={className} style={style} dangerouslySetInnerHTML={{ __html: withExternalLinks(html) }} />;
}

export default RichText;
